import { useCallback, useEffect, useState } from 'react';
import { store, useCurrency, useCustomer } from '@backpackjs/storefront';

export function useDataLayerAccount({
  DEBUG,
  generateUserProperties,
  userDataEvent,
  userDataEventTriggered,
}) {
  const customer = useCustomer();
  const currencyCode = useCurrency();

  const accessToken = store.recoil.useRecoilValue(store.state.accessToken);
  const [loggedIn, setLoggedIn] = useState(false);
  const [registered, setRegistered] = useState(false);

  const loggedInEvent = useCallback(({ userProperties }) => {
    const event = {
      event: 'dl_login',
      user_properties: userProperties,
    };

    window.ElevarDataLayer = window.ElevarDataLayer ?? [];
    window.ElevarDataLayer.push(event);
    if (DEBUG) console.log(`DataLayer:elevar:${event.event}`, event);
    setLoggedIn(false);
  }, []);

  const registeredEvent = useCallback(({ userProperties }) => {
    const event = {
      event: 'dl_sign_up',
      user_properties: userProperties,
    };

    window.ElevarDataLayer = window.ElevarDataLayer ?? [];
    window.ElevarDataLayer.push(event);
    if (DEBUG) console.log(`DataLayer:elevar:${event.event}`, event);
    setRegistered(false);
  }, []);

  const getUserPropertiesAndTriggerLoggedInEvents = useCallback(
    async ({
      customer: _customer,
      currencyCode: _currencyCode,
      accessToken: _accessToken,
    }) => {
      const userProperties = await generateUserProperties({
        customer: _customer,
        accessToken: _accessToken.token,
      });
      loggedInEvent({ userProperties });
      setTimeout(() => {
        userDataEvent({ currencyCode: _currencyCode, userProperties });
      }, 1000);
    },
    []
  );

  const getUserPropertiesAndTriggerRegisterEvents = useCallback(
    async ({
      customer: _customer,
      currencyCode: _currencyCode,
      accessToken: _accessToken,
    }) => {
      const userProperties = await generateUserProperties({
        customer: _customer,
        accessToken: _accessToken.token,
      });
      registeredEvent({ userProperties });
      setTimeout(() => {
        userDataEvent({ currencyCode: _currencyCode, userProperties });
      }, 1000);
    },
    []
  );

  // Subscribe to PubSub topics for 'dl_login' and 'dl_sign_up' events
  useEffect(() => {
    const customerLoggedIn = PubSub.subscribe(
      'CUSTOMER_LOGGED_IN',
      async () => {
        setLoggedIn(true);
      }
    );
    const customerRegistered = PubSub.subscribe(
      'CUSTOMER_REGISTERED',
      async () => {
        setRegistered(true);
      }
    );
    return () => {
      if (customerLoggedIn) {
        PubSub.unsubscribe(customerLoggedIn);
      }
      if (customerRegistered) {
        PubSub.unsubscribe(customerRegistered);
      }
    };
  }, []);

  // Generate new base data after customer login and customer profile is ready
  // Trigger 'dl_user_data' and 'dl_login' events
  useEffect(() => {
    if (
      !customer ||
      !currencyCode ||
      !accessToken?.token ||
      !loggedIn ||
      !userDataEventTriggered
    )
      return;
    getUserPropertiesAndTriggerLoggedInEvents({
      customer,
      currencyCode,
      accessToken,
    });
  }, [
    !!customer,
    !!currencyCode,
    accessToken?.token,
    loggedIn,
    userDataEventTriggered,
  ]);

  // Generate new base data after customer register and customer profile is ready
  // Trigger 'dl_user_data' and 'dl_sign_up' events
  useEffect(() => {
    if (
      !customer ||
      !currencyCode ||
      !accessToken?.token ||
      !registered ||
      !userDataEventTriggered
    )
      return;
    getUserPropertiesAndTriggerRegisterEvents({
      customer,
      currencyCode,
      accessToken,
    });
  }, [
    !!customer,
    !!currencyCode,
    accessToken?.token,
    registered,
    userDataEventTriggered,
  ]);
}
